import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Box, Card, MenuItem, Popover, TextField, Typography} from "@mui/material";
import {WarehouseRounded} from "@mui/icons-material";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import Api from "../../../../../../../core/services/api/api";
import {Checkbox} from "../../../../../../components/base/checkbox/checkbox";
import {generateBusinessName, generateWarehouseName} from "../../../../../../../core/services/utils/name-generator";
import EnvService from "../../../../../../../core/services/env";


const LookupSearchViewWarehouseSelect = ({value: _value, setValue}) => {
    const [anchor, setAnchor] = useState(null);
    const [_partners, setPartners] = useState([]);
    const isMounted = useIsMounted();
    const fetched = useRef(false);
    const [selectedPartnerId, setSelectedPartnerId] = useState(_value?.partnerId);
    const [selectedWarehouseIds, setSelectedWarehouseIds] = useState(_value?.warehouses?.map(e => e.id));

    const partners = useMemo(() => _partners.map(e => ({
        ...e,
        name: EnvService.enableDemoNameMasking ? generateBusinessName(e.partner?.name ?? "") : e.partner?.name ?? "",
        partner: {
            ...(e.partner ?? {}),
            name: EnvService.enableDemoNameMasking ? generateBusinessName(e.partner?.name ?? "") : e.partner?.name ?? "",
        },
        warehouses: e.warehouses.map(w => ({
            ...w,
            name: EnvService.enableDemoNameMasking ? generateWarehouseName(w.name ?? "") : w.name ?? "",
        })),
    })), [_partners]);

    const value=  useMemo(() => {
        if (!_value)
            return null;
        const partner = partners.find(e => e.partner.publicKey === _value.partnerId);
        return {
            ..._value,
            partnerName: partner?.partner.name ?? "",
            warehouses: _value.warehouses?.map(w => ({
                ...w,
                name: partner?.warehouses?.find(ww => ww.id === w.id)?.name ?? "",
            }))
        }
    }, [_value, partners]);

    /**
     * Fetches the partners and their warehouses
     * @type {(function(): void)}
     */
    const getPartnerAndWarehouses = useCallback(async () => {
        const response = await Api.partnerConnectionGetPartnersAndWarehouses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            fetched.current = true;
            let partners = [...(response?.data ?? [])];
            partners = partners.filter(e => e.partner.isYourPartner);
            setPartners(partners);
        }
    }, [isMounted])

    /**
     * When the anchor changes, fetch the partners and warehouses
     */
    useEffect(() => {
        if (fetched.current)
            return
        void getPartnerAndWarehouses();
    }, [anchor, getPartnerAndWarehouses]);

    /**
     * When the value changes, set the selected partner and warehouse
     */
    useEffect(() => {
        if (!anchor)
            return;
        setSelectedPartnerId(_value?.partnerId)
        setSelectedWarehouseIds(_value?.warehouses?.map(e => e.id))
    }, [_value?.partnerId, _value?.warehouses, anchor]);

    const warehousesOfSelectedPartner = useMemo(() => partners.find(e => e.partner.publicKey === selectedPartnerId)?.warehouses ?? [], [partners, selectedPartnerId])

    /**
     * Saves the selected partner and warehouse
     * @type {(function(): void)}
     */
    const saveValues = useCallback(() => {
        setValue({
            partnerId: selectedPartnerId,
            partnerName: partners.find(e => e.partner.publicKey === selectedPartnerId)?.partner?.name,
            warehouses: warehousesOfSelectedPartner.filter(e => selectedWarehouseIds.includes(e.id)),
        })
        setAnchor(null)
    }, [partners, selectedPartnerId, selectedWarehouseIds, setValue, warehousesOfSelectedPartner])

    return (
        <>
            <Card
                onClick={(e) => setAnchor(e.currentTarget)}
                variant={'elevation'}
                sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: 'none',
                    backgroundColor: 'secondary.light',
                    borderRadius: 1,
                    paddingInline: 2,
                    paddingBlock: 1,
                }}
            >
                <WarehouseRounded color={'secondary'}/>
                <Typography variant={'body2'} fontWeight={600}>
                    {
                        !!value
                            ? `${value.warehouses?.map(e => `${e.name} (${e.code})`)?.join(', ') ?? ""}`
                            : "Select the warehouses that you want to see availabilities for"
                    }
                    {
                        !!value &&
                        <Typography
                            component={'span'}
                            sx={{display: 'inline-block', marginInlineStart: 1}}
                            variant={'caption'}
                            fontWeight={600}
                            color={'secondary'}
                        >
                            ({value.partnerName ?? ""})
                        </Typography>
                    }
                </Typography>
            </Card>
            <Popover
                id={'warehouse-selection-popover'}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                anchorEl={anchor}
                elevation={1}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        width: 'min(95vw, 400px)',
                        padding: 1,
                    }}
                >
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Select Warehouses
                    </Typography>
                    <Typography variant={'caption'}>
                        Select the warehouses that you would want to see availabilities for.
                    </Typography>
                    <TextField
                        sx={{marginBlock: 1}}
                        select
                        fullWidth
                        size={'small'}
                        label={'Partner'}
                        value={selectedPartnerId ?? ''}
                        onChange={(e) => setSelectedPartnerId(e.target.value) || setSelectedWarehouseIds(undefined)}
                        SelectProps={{
                            renderValue: (value) => partners.find(e => e.partner.publicKey === value)?.partner?.name ?? "",
                        }}
                    >
                        {
                            partners.map(partner => (
                                <MenuItem key={partner.partner?.publicKey} value={partner.partner?.publicKey}>
                                    {partner.partner?.name ?? ""}
                                </MenuItem>
                            ))
                        }
                    </TextField>
                    {
                        !!selectedPartnerId &&
                        <TextField
                            select
                            fullWidth
                            size={'small'}
                            label={'Warehouses'}
                            value={selectedWarehouseIds ?? []}
                            onChange={(e) => setSelectedWarehouseIds(e.target.value)}
                            SelectProps={{
                                multiple: true,
                                renderValue: (value) => {
                                    if (!value)
                                        return "";
                                    const selectedWarehouses = warehousesOfSelectedPartner.filter(e => value.includes(e.id));
                                    return selectedWarehouses.map(e => `${e.name} (${e.code})`).join(', ')
                                },
                            }}
                        >
                            {
                                warehousesOfSelectedPartner.map(warehouse => (
                                    <MenuItem
                                        key={`${warehouse.id}-${selectedWarehouseIds?.includes(warehouse.id)}`}
                                        value={warehouse.id}
                                        sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
                                    >
                                        <span>
                                            {warehouse.name ?? ""} ({warehouse.code ?? ""})
                                        </span>
                                        <Checkbox
                                            checked={selectedWarehouseIds?.includes(warehouse.id)}
                                            color={'secondary'}
                                            formControlLabelProps={{
                                                sx: {margin: 0,}
                                            }}
                                        />
                                    </MenuItem>
                                ))
                            }
                        </TextField>
                    }
                    <div className={'mt-2'}/>
                    <SaveOrRollback
                        column={false}
                        showText
                        show={{cancel: true, save: !!selectedWarehouseIds && !!selectedPartnerId,}}
                        onSave={saveValues}
                        onRollback={() => setAnchor(null)}
                    />
                    <div className={'mt-1'}/>
                </Box>
            </Popover>
        </>
    );
}

export default LookupSearchViewWarehouseSelect;
